export default class ArticleNavigation {
  constructor(options = {}) {
    // header must be resolved before settings so offset is correct
    this.header = document.querySelector(".js-header");
    this.settings = {
      speed: 1000,
      offset: this.header?.offsetHeight || 0,
      currentClass: "js-anchor-active",
      updateHash: true,
      classToParent: true,
      ...options,
    };

    this.navbar = document.querySelector(".contents-menu");
    this.rootBlock = this.navbar?.closest(".js-sidebar-nav");
    this.items = Array.from(this.navbar.querySelectorAll("a:not(.externals)"));
    this.sections = [];
    this.sectionMeta = [];
    this.activeLink = null; // direct reference to active <a>, avoids forEach on each change
    this.hashNow = undefined;
    this.rafRunning = false;
    this.isClickScrolling = false;
    this.isDesktopActive = false;
    this.clickScrollTimeout = null;

    this.boundOnScrollRaf = this.onScrollRaf.bind(this);
    this.boundDebouncedResize = this.debounce(() => this.handleResize(), 120);
    // pushState is rate-limited by browsers — debounce to avoid jank on fast scroll
    this.debouncedPushHash = this.debounce((hash) => {
      if (this.hashNow !== hash) {
        this.hashNow = hash;
        history.pushState({}, "", hash);
      }
    }, 150);

    this.collectSections();
    this.bindBaseEvents();
  }

  bindBaseEvents() {
    this.items.forEach((item) =>
      item.addEventListener("click", (e) => this.handleClick(e)),
    );
  }

  initDesktopListeners() {
    if (this.isDesktopActive) return;
    window.addEventListener("scroll", this.boundOnScrollRaf);
    window.addEventListener("resize", this.boundDebouncedResize);
    this.calcSectionMeta();
    this.isDesktopActive = true;
    this.scrollPage();
  }

  onScrollRaf() {
    if (!this.isDesktopActive || this.rafRunning) return;
    this.rafRunning = true;
    requestAnimationFrame(() => {
      this.scrollPage();
      this.rafRunning = false;
    });
  }

  collectSections() {
    this.sections = this.items
      .map((item) =>
        document.getElementById(item.getAttribute("href").replace("#", "")),
      )
      .filter(Boolean);
  }

  calcSectionMeta() {
    this.sectionMeta = this.sections.map((sec) => ({
      id: sec.id,
      top: sec.offsetTop,
    }));
  }

  handleResize() {
    this.settings.offset = this.header?.offsetHeight || 0;
    this.calcSectionMeta();
    this.scrollPage();
  }

  handleClick(e) {
    e.preventDefault();
    const hash = e.currentTarget.getAttribute("href");
    const target = document.getElementById(hash.slice(1));
    if (!target) return;

    const absoluteTop = target.getBoundingClientRect().top + window.scrollY;
    const position = absoluteTop - this.settings.offset - 90;

    this.isClickScrolling = true;
    clearTimeout(this.clickScrollTimeout);

    window.scrollTo({ top: position, behavior: "smooth" });

    if (this.settings.updateHash) {
      this.hashNow = hash;
      history.pushState({}, "", hash);
    }

    this.clickScrollTimeout = setTimeout(() => {
      this.isClickScrolling = false;
      this.scrollPage();
    }, this.settings.speed + 50);
  }

  scrollPage() {
    if (!this.isDesktopActive || this.isClickScrolling) return;

    const winTop = window.scrollY;
    const winBot = winTop + window.innerHeight;
    const offset = this.settings.offset + 90;
    const isNearPageBottom =
      document.documentElement.scrollHeight - winBot < 120;

    // iterate backward — first match is the deepest section we've scrolled past
    let activeId = undefined;
    for (let i = this.sectionMeta.length - 1; i >= 0; i--) {
      const meta = this.sectionMeta[i];
      const isLast = i === this.sectionMeta.length - 1;
      if ((isLast && isNearPageBottom) || meta.top <= winTop + offset) {
        activeId = meta.id;
        break;
      }
    }

    if (!activeId) return;

    const newLink = this.navbar.querySelector(`a[href="#${activeId}"]`);
    if (!newLink || newLink === this.activeLink) return;

    this.setActive(newLink);

    if (this.settings.updateHash) {
      this.debouncedPushHash(`#${activeId}`);
    }
  }

  setActive(link) {
    const cls = this.settings.currentClass;

    if (this.activeLink) {
      this.activeLink.parentElement.classList.remove(cls);
      this.activeLink.closest(".contents-menu > li")?.classList.remove(cls);
    }

    link.parentElement.classList.add(cls);
    if (this.settings.classToParent) {
      link.closest(".contents-menu > li")?.classList.add(cls);
    }

    this.scrollNavToItem(link.parentElement);
    this.activeLink = link;
  }

  scrollNavToItem(item) {
    const menu = item.closest(".contents-menu");
    if (!menu) return;
    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;
    const menuTop = menu.scrollTop;
    const menuBottom = menuTop + menu.clientHeight;
    if (itemTop < menuTop) {
      menu.scrollTop = itemTop - 5;
    } else if (itemBottom > menuBottom) {
      menu.scrollTop = itemBottom - menu.clientHeight + 5;
    }
  }

  debounce(fn, ms) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  destroyDesktopListeners() {
    if (!this.isDesktopActive) return;
    window.removeEventListener("scroll", this.boundOnScrollRaf);
    window.removeEventListener("resize", this.boundDebouncedResize);
    if (this.activeLink) {
      this.activeLink.parentElement.classList.remove(this.settings.currentClass);
      this.activeLink.closest(".contents-menu > li")?.classList.remove(this.settings.currentClass);
      this.activeLink = null;
    }
    this.isDesktopActive = false;
  }
}
